import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, StatusBadge } from "@/components/common";
import { fmt } from "@/lib/api";

export function CapChips({ caps, catalog, testId }) {
  const [all, setAll] = useState(false);
  const names = caps.map((c) => catalog.capabilities.find((x) => x.code === c)?.name || c);
  const shown = all ? names : names.slice(0, 4);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={testId}>
      {shown.map((n) => <span key={n} className="rounded-full border border-[var(--po-border)] bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">{n}</span>)}
      {names.length > 4 && <button type="button" data-testid={`${testId}-more`} onClick={() => setAll(!all)} className="text-[11px] text-slate-400 hover:text-slate-600">{all ? "Show less" : `+${names.length - 4} more`}</button>}
    </div>
  );
}

function AddonRow({ a, isNew, catalog, onSave, onCancel, onDelete }) {
  const [edit, setEdit] = useState(isNew);
  const [f, setF] = useState(a);
  const set = (k, v) => setF({ ...f, [k]: v });
  if (!edit) return (
    <tr data-testid={`addon-row-${a.code}`}>
      <td className="font-mono text-xs text-slate-500">{a.code}</td><td className="font-medium text-slate-900">{a.name}{a.description && <div className="text-xs font-normal text-slate-500" data-testid={`addon-desc-${a.code}`}>{a.description}</div>}</td>
      <td className="po-num">{a.price_placeholder ? <StatusBadge tone="warning" label="Placeholder" /> : fmt(a.monthly_price, "USD")}</td>
      <td className="text-sm text-slate-600">{a.unit}</td>
      <td className="text-right whitespace-nowrap"><Button size="sm" variant="outline" data-testid={`addon-edit-${a.code}`} onClick={() => setEdit(true)}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
        {onDelete && <Button size="sm" variant="ghost" className="ml-1 text-rose-600" data-testid={`addon-delete-${a.code}`} onClick={() => window.confirm(`Delete add-on "${a.name}"? Past invoices keep their billed lines.`) && onDelete(a)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
    </tr>);
  const done = async () => { if (await onSave(f, isNew)) { isNew ? onCancel() : setEdit(false); } };
  return (
    <tr data-testid={`addon-editing-${a.code || "new"}`}>
      <td><Input data-testid="addon-code-input" disabled={!isNew} value={f.code} onChange={(e) => set("code", e.target.value)} /></td>
      <td className="space-y-1"><Input data-testid="addon-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} />
        <Input data-testid="addon-desc-input" maxLength={300} placeholder="Description shown to tenants on the Billing page" value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></td>
      <td><Input type="number" data-testid="addon-price-input" value={f.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} /></td>
      <td><Input data-testid="addon-unit-input" value={f.unit} onChange={(e) => set("unit", e.target.value)} /></td>
      <td><div className="flex items-center justify-end gap-2">
        {!a.bundle && <NativeSelect testId="addon-capability-select" value={f.capability || ""} onChange={(v) => set("capability", v)} options={[{ value: "", label: "No capability" }, ...catalog.capabilities.map((c) => ({ value: c.code, label: c.name }))]} />}
        <Button size="sm" data-testid="addon-save-button" disabled={!f.code || !f.name} onClick={done}>Save</Button>
        <Button size="sm" variant="outline" data-testid="addon-cancel-button" onClick={() => { setF(a); isNew ? onCancel() : setEdit(false); }}>Cancel</Button></div></td>
    </tr>);
}

export function AddonsTable({ addons, catalog, onSave, onDelete }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="overflow-x-auto">
      <table className="po-table w-full" data-testid="addons-table">
        <thead><tr><th>Code</th><th>Name</th><th className="po-num">Monthly price (USD)</th><th>Unit</th><th /></tr></thead>
        <tbody>
          {addons.map((a) => <AddonRow key={a.code} a={a} catalog={catalog} onSave={onSave} onDelete={onDelete} />)}
          {adding && <AddonRow isNew a={{ code: "", name: "", monthly_price: 0, unit: "workspace", capability: "" }} catalog={catalog} onSave={onSave} onCancel={() => setAdding(false)} />}
        </tbody>
      </table>
      {!adding && <div className="p-4"><Button size="sm" variant="outline" data-testid="addon-add-button" onClick={() => setAdding(true)}><Plus className="mr-1 h-3.5 w-3.5" />Add add-on</Button></div>}
    </div>
  );
}
